
import { useState } from 'react';
import type { BusinessFormData } from '../page';

const categories = [
  'Restaurants & Dining',
  'Shopping & Retail',
  'Health & Wellness',
  'Professional Services',
  'Home Services',
  'Automotive',
  'Beauty & Spa',
  'Entertainment',
  'Education',
  'Real Estate',
  'Financial Services',
  'Technology',
  'Other',
];

interface ProfileStepProps {
  formData: BusinessFormData;
  setFormData: React.Dispatch<React.SetStateAction<BusinessFormData>>;
  onComplete: (data: BusinessFormData) => void;
  onBack: () => void;
}

export default function ProfileStep({ formData, setFormData, onComplete }: ProfileStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState(false);

  const handleInputChange = (field: keyof BusinessFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleHoursChange = (index: number, field: string, value: string | boolean) => {
    const newHours = [...formData.hours];
    newHours[index] = { ...newHours[index], [field]: value };
    setFormData(prev => ({ ...prev, hours: newHours }));
  };

  const copyMondayToAll = () => {
    const monday = formData.hours[0];
    const newHours = formData.hours.map(day => ({
      ...day,
      open: monday.open,
      close: monday.close,
      closed: monday.closed,
    }));
    setFormData(prev => ({ ...prev, hours: newHours }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(Array.from(e.target.files));
  };

  const handleFiles = (files: File[]) => {
    if (formData.photos.length + files.length > 10) {
      setErrors(prev => ({ ...prev, photos: 'Maximum 10 photos allowed' }));
      return;
    }
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, photos: 'Only image files are allowed' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, photos: 'Each photo must be less than 5MB' }));
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newPhoto = {
          id: Math.random().toString(36).substr(2, 9),
          url: ev.target?.result as string,
          name: file.name,
          type: file.type,
        };
        setFormData(prev => ({ ...prev, photos: [...prev.photos, newPhoto] }));
        setErrors(prev => ({ ...prev, photos: '' }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(Array.from(e.dataTransfer.files));
  };

  const removePhoto = (photoId: string) => {
    setFormData(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoId) }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.ownerName.trim()) newErrors.ownerName = 'Owner name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.zip.trim()) newErrors.zip = 'ZIP code is required';
    if (!formData.description.trim()) newErrors.description = 'Business description is required';
    if (formData.description.length > 500) newErrors.description = 'Description must be 500 characters or less';
    if (formData.photos.length === 0) newErrors.photos = 'At least 1 photo is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email';

    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) newErrors.phone = 'Please enter a valid phone number';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    onComplete(formData);
  };

  return (
    <section className="pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-store-2-line text-teal-600"></i>
            </div>
            <span className="text-sm font-semibold text-teal-700">Step 2 of 3</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Create Your Business Profile</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Fill in your business details. All fields marked with * are required.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Business Details */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-500">
                Business Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="Enter your business name"
                  />
                  {errors.businessName && <p className="mt-1 text-sm text-red-500">{errors.businessName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => handleInputChange('ownerName', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.ownerName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="Your full name"
                  />
                  {errors.ownerName && <p className="mt-1 text-sm text-red-500">{errors.ownerName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="https://www.yourbusiness.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.category ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="123 Main Street"
                  />
                  {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    className={`w-full px-4 py-3 border ${errors.zip ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="95678"
                  />
                  {errors.zip && <p className="mt-1 text-sm text-red-500">{errors.zip}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year Established</label>
                  <input
                    type="text"
                    value={formData.yearEstablished}
                    onChange={(e) => handleInputChange('yearEstablished', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="2020"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Employees</label>
                  <input
                    type="text"
                    value={formData.numberOfEmployees}
                    onChange={(e) => handleInputChange('numberOfEmployees', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="1-10"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key Services/Products</label>
                  <input
                    type="text"
                    value={formData.services}
                    onChange={(e) => handleInputChange('services', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="e.g., Web Design, SEO, Digital Marketing"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-500">
                Business Description
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="A short, catchy phrase about your business"
                    maxLength={100}
                  />
                  <p className="mt-1 text-xs text-gray-500">{formData.tagline.length}/100 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Description * (Max 500 characters)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={6}
                    maxLength={500}
                    className={`w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    placeholder="Tell customers about your business, what makes you unique, and why they should choose you..."
                  />
                  <div className="mt-1 flex justify-between items-center">
                    {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
                    <p className={`text-xs ${formData.description.length > 500 ? 'text-red-500' : 'text-gray-500'} ml-auto`}>
                      {formData.description.length}/500 characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Notes</label>
                  <textarea
                    value={formData.specialNotes}
                    onChange={(e) => handleInputChange('specialNotes', e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Any special offers, certifications, awards, or important information..."
                  />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-500">
                Business Photos *
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Upload 1-10 photos of your business. First photo will be your main image.
              </p>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed ${dragActive ? 'border-teal-500 bg-teal-50' : errors.photos ? 'border-red-500' : 'border-gray-300'} rounded-lg p-8 text-center transition-colors`}
              >
                <input type="file" id="photo-upload-profile" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
                <label htmlFor="photo-upload-profile" className="cursor-pointer">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-image-add-line text-3xl text-teal-500"></i>
                  </div>
                  <p className="text-gray-700 font-medium mb-2">Drag and drop photos here, or click to browse</p>
                  <p className="text-sm text-gray-500">PNG, JPG up to 5MB each (Max 10 photos)</p>
                </label>
              </div>
              {errors.photos && <p className="mt-2 text-sm text-red-500">{errors.photos}</p>}

              {formData.photos.length > 0 && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.photos.map((photo, index) => (
                    <div key={photo.id} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                      </div>
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-teal-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          Main Photo
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 cursor-pointer"
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hours */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-500">
                Operating Hours
              </h3>
              <div className="space-y-4">
                {formData.hours.map((hour, index) => (
                  <div key={hour.day} className="flex items-center gap-4">
                    <div className="w-28 font-medium text-gray-700 text-sm">{hour.day}</div>
                    <div className="flex items-center gap-4 flex-1">
                      <input
                        type="time"
                        value={hour.open}
                        onChange={(e) => handleHoursChange(index, 'open', e.target.value)}
                        disabled={hour.closed}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <span className="text-gray-500 text-sm">to</span>
                      <input
                        type="time"
                        value={hour.close}
                        onChange={(e) => handleHoursChange(index, 'close', e.target.value)}
                        disabled={hour.closed}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hour.closed}
                          onChange={(e) => handleHoursChange(index, 'closed', e.target.checked)}
                          className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">Closed</span>
                      </label>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={copyMondayToAll}
                  className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy Monday to All Days
                </button>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-500">
                Social Media
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-facebook-fill text-[#1877F2] mr-2"></i>Facebook URL
                  </label>
                  <input
                    type="url"
                    value={formData.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="https://facebook.com/yourbusiness"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-instagram-line text-pink-600 mr-2"></i>Instagram URL
                  </label>
                  <input
                    type="url"
                    value={formData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="https://instagram.com/yourbusiness"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-linkedin-fill text-[#0A66C2] mr-2"></i>LinkedIn URL
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="https://linkedin.com/company/yourbusiness"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-6 border-t">
              <button
                type="submit"
                className="w-full py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Continue to Select Plan
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
